import React, { useState, useEffect, memo } from 'react';
import { getHousekeeperById, updateHousekeeper, updateResumeStatus, getHousekeeperExt, saveChatText,
  uploadAudioRecord, uploadChatRecordWithTranscription, uploadWorkImage, uploadCookImage,
  uploadCertificateImage, uploadNannyCert, deleteNannyCert, uploadBankCardImageWithExtraction,
  deleteAudioRecord, deleteChatRecord, deleteWorkImage, deleteCookImage, deleteCertificateImage } from '../api/housekeeperApi';
import { uploadImageToCos, uploadAudioToCos } from '../api/cosApi';
import { parseJsonArray } from '../utils/jsonParser';
import './HousekeeperEditPage2.css';

const UploadSection = memo(({ title, files, onUpload, onDelete, folder, apiFn, showDelete = true, showText = false, chatText, onChatTextChange, onSaveChatText, userId }) => (
  <div className="upload-section">
    <div className="section-header">
      <span className="section-title">{title}</span>
      <label className="upload-btn">
        <span className="plus-icon">+</span>
        <input type="file" accept={folder === 'audio' ? '.mp3,.wav,.m4a,.aac' : 'image/*'} multiple={folder !== 'audio'} onChange={(e) => { const fileList = Array.from(e.target.files); fileList.forEach(f => onUpload(f, folder, apiFn, userId)); }} style={{ display: 'none' }} />
      </label>
    </div>
    {files.length > 0 ? (
      <div className="media-grid">
        {files.map((item, idx) => (
          <div key={idx} className="media-item">
            {folder === 'audio' ? (
              <div className="audio-item-inner">
                <span className="audio-icon">🎵</span>
                <span className="audio-name">{item.filename || `录音${idx + 1}`}</span>
                <audio controls src={item.url} style={{ width: '100%', height: '30px' }} />
              </div>
            ) : (
              <img className="thumbnail" src={item.url || item} alt={title} />
            )}
            {showDelete && <button className="delete-btn" onClick={() => onDelete(item.url || item)}>✕</button>}
          </div>
        ))}
      </div>
    ) : (
      <div className="empty-tip">暂无{folder === 'audio' ? '录音' : '图片'}，点击右上角+上传</div>
    )}
    {showText && (
      <div className="chat-text-section">
        <div className="section-subtitle">聊天记录文字（可直接粘贴）</div>
        <textarea className="chat-textarea" value={chatText} onChange={onChatTextChange} onBlur={onSaveChatText} placeholder="粘贴聊天记录文字..." />
        <button className="chat-text-save" onClick={onSaveChatText}>保存文字</button>
      </div>
    )}
  </div>
));

export default function HousekeeperEditPage2({ id, navigateTo }) {
  const [audioFiles, setAudioFiles] = useState([]);
  const [chatImages, setChatImages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [workImages, setWorkImages] = useState([]);
  const [cookImages, setCookImages] = useState([]);
  const [certImages, setCertImages] = useState([]);
  const [nannyCertUrl, setNannyCertUrl] = useState('');
  const [role, setRole] = useState('');
  const [certName, setCertName] = useState('育婴师证');
  const [bankCardImages, setBankCardImages] = useState([]);
  const [originalChatText, setOriginalChatText] = useState('');
  const [expectedSalary, setExpectedSalary] = useState('');
  const [abilitySalary, setAbilitySalary] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (id) loadData(id); }, [id]);

  const loadData = async (id) => {
    try {
      const res = await getHousekeeperById(id);
      const h = res.data;
      // 解析 audio_records（兼容旧数据）
      const audioRecords = parseJsonArray(h.audio_records).map(item =>
        typeof item === 'string' ? { url: item, filename: '录音' } : item
      );
      setAudioFiles(audioRecords);
      setChatImages(parseJsonArray(h.chat_records));
      setWorkImages(parseJsonArray(h.work_url));
      setCookImages(parseJsonArray(h.cook_url));
      setCertImages(parseJsonArray(h.certificate_url));
      setNannyCertUrl(h.nanny_cert_url || '');
      setRole(h.role || '');
      // 根据角色设置证书名称
      if (h.role === '月嫂') {
        setCertName('母婴护理证');
      } else {
        setCertName('育婴师证');
      }
      setExpectedSalary(h.expected_salary ? String(h.expected_salary) : '');
      setAbilitySalary(h.ability_salary ? String(h.ability_salary) : '');

      try {
        const extRes = await getHousekeeperExt(id);
        if (extRes.data?.bank_card_url) {
          setBankCardImages(parseJsonArray(extRes.data.bank_card_url));
        }
        if (extRes.data?.chat_text) {
          setChatText(extRes.data.chat_text);
          setOriginalChatText(extRes.data.chat_text);
        }
      } catch (e) { /* ext may not exist yet */ }
    } catch (e) { console.error('加载失败:', e); }
  };

  const doUpload = async (file, folder, apiFn, userId) => {
    // 工作展示和厨艺展示最多15张
    if ((folder === 'work-image' && workImages.length >= 15) ||
        (folder === 'cook-image' && cookImages.length >= 15)) {
      alert('最多允许上传15张图片');
      return;
    }
    setUploading(true);
    try {
      const isAudio = folder === 'audio';
      const accessUrl = isAudio
        ? await uploadAudioToCos(file, userId)
        : await uploadImageToCos(file, folder, userId);
      // 音频文件名去除空格，避免语音识别失败
      const originalFilename = isAudio ? (file.name || '').replace(/\s+/g, '') : null;
      await apiFn(id, accessUrl, originalFilename);
      loadData(id);
      alert('上传成功');
    } catch (e) {
      const msg = e?.response?.data?.detail || '上传失败';
      alert(msg);
    }
    finally { setUploading(false); }
  };

  const doDelete = (apiFn) => async (url) => {
    if (!window.confirm('确定删除？')) return;
    try { await apiFn(id, url); loadData(id); alert('删除成功'); }
    catch (e) { alert('删除失败'); }
  };

  const saveChatTextInput = async () => {
    if (!chatText.trim()) return;
    // Only send the new (delta) portion of text
    const newText = chatText.startsWith(originalChatText)
      ? chatText.slice(originalChatText.length)
      : chatText;
    if (!newText.trim()) return; // 没有新增内容，静默返回
    try {
      await saveChatText(id, newText.trim());
      setOriginalChatText(chatText);
      alert('保存成功');
    } catch (e) { alert('保存失败'); }
  };

  const saveSalaryInfo = async () => {
    try {
      const updateData = {};
      if (expectedSalary) updateData.expected_salary = parseInt(expectedSalary);
      else updateData.expected_salary = null;
      await updateHousekeeper(id, updateData);
      if (abilitySalary) {
        await updateHousekeeper(id, { ability_salary: parseInt(abilitySalary) });
      }
    } catch (e) { console.error('保存薪资失败:', e); }
  };

  const handlePrevPage = async () => {
    const doSave = window.confirm('是否保存当前页面的信息？');
    if (doSave) {
      await saveSalaryInfo();
    }
    navigateTo('edit1', { id: id, mode: 'continue' });
  };
  const handleGenerate = async () => {
    // 所有角色必须上传指定证书
    if (!nannyCertUrl) {
      alert(`该阿姨角色为${role}，请上传${certName}！`);
      return;
    }

    await saveSalaryInfo();
    try {
      await updateResumeStatus(id, 'pending');
      alert('简历已生成');
      navigateTo('home');
    } catch (e) { alert(e?.response?.data?.detail || '操作失败'); }
  };

  const doUploadNannyCert = async (file, folder, apiFn, userId) => {
    setUploading(true);
    try {
      const accessUrl = await uploadImageToCos(file, folder, userId);
      await uploadNannyCert(id, accessUrl);
      loadData(id);
      alert('上传成功');
    } catch (e) { alert('上传失败'); }
    finally { setUploading(false); }
  };

  const doDeleteNannyCert = async () => {
    if (!window.confirm('确定删除育婴师证书？')) return;
    try {
      await deleteNannyCert(id, nannyCertUrl);
      setNannyCertUrl('');
      alert('删除成功');
    } catch (e) { alert('删除失败'); }
  };

  return (
    <div className="edit2-container">
      <div className="edit-header">
        <button className="edit-back" onClick={() => navigateTo('edit1', { id })}>←</button>
        <span className="edit-title">多媒体信息录入</span>
        <button className="edit-close" onClick={() => navigateTo('home')}>✕</button>
      </div>

      <div className="edit-body">
        <UploadSection title="面试录音" files={audioFiles} folder="audio" apiFn={uploadAudioRecord} onUpload={doUpload} onDelete={doDelete(deleteAudioRecord)} userId={JSON.parse(localStorage.getItem('userInfo') || '{}').user_id} />
        <UploadSection title="聊天记录" files={chatImages.map(url => ({url}))} folder="chat-image" apiFn={uploadChatRecordWithTranscription} onUpload={doUpload} onDelete={doDelete(deleteChatRecord)} showText={true} chatText={chatText} onChatTextChange={(e) => setChatText(e.target.value)} onSaveChatText={saveChatTextInput} />
        <UploadSection title="宝宝护理" files={workImages.map(url => ({url}))} folder="work-image" apiFn={uploadWorkImage} onUpload={doUpload} onDelete={doDelete(deleteWorkImage)} />
        <UploadSection title="厨艺展示" files={cookImages.map(url => ({url}))} folder="cook-image" apiFn={uploadCookImage} onUpload={doUpload} onDelete={doDelete(deleteCookImage)} />

        <div className="upload-section">
            <div className="section-header">
              <span className="section-title">{certName}</span>
              <label className="upload-btn">
                <span className="plus-icon">+</span>
                <input type="file" accept="image/*" multiple={false} onChange={(e) => { const file = e.target.files[0]; if (file) doUploadNannyCert(file, 'nanny-cert', uploadNannyCert, JSON.parse(localStorage.getItem('userInfo') || '{}').user_id); }} style={{ display: 'none' }} />
              </label>
            </div>
            {nannyCertUrl ? (
              <div className="media-grid">
                <div className="media-item">
                  <img className="thumbnail" src={nannyCertUrl} alt={certName} />
                  <button className="delete-btn" onClick={doDeleteNannyCert}>✕</button>
                </div>
              </div>
            ) : (
              <div className="empty-tip">暂无图片，点击右上角+上传</div>
            )}
            <div className="nanny-cert-hint">{role}必须上传{certName}</div>
          </div>

        <UploadSection title="资质证书" files={certImages.map(url => ({url}))} folder="certificate-image" apiFn={uploadCertificateImage} onUpload={doUpload} onDelete={doDelete(deleteCertificateImage)} />
        <UploadSection title="银行卡及个人流水" files={bankCardImages.map(url => ({url}))} folder="salary-img" apiFn={uploadBankCardImageWithExtraction} onUpload={doUpload} onDelete={doDelete(deleteChatRecord)} showDelete={false} />

        <div className="form-section salary-section">
          <div className="section-title">期望薪资（元/月）</div>
          <input className="form-input salary-input" type="number" value={expectedSalary} onChange={(e) => setExpectedSalary(e.target.value)} placeholder="请输入期望薪资" />
        </div>
        <div className="form-section salary-section">
          <div className="section-title">历史薪资（元/月）</div>
          <input className="form-input salary-input" type="number" value={abilitySalary} onChange={(e) => setAbilitySalary(e.target.value)} placeholder="AI识别平均工资" />
        </div>
      </div>

      <div className="action-bar">
        <button className="action-btn" onClick={handlePrevPage}>上一页</button>
        <button className="action-btn primary" onClick={handleGenerate}>生成简历</button>
      </div>

      {uploading && (
        <div className="uploading-overlay">
          <div className="uploading-content">
            <div className="spinner"></div>
            <div className="uploading-text">正在上传...</div>
          </div>
        </div>
      )}
    </div>
  );
}